import React, { useState } from 'react'
import { Card, CardContent } from '../ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs'
import { Badge } from '../ui/badge'
import { Dialog, DialogContent } from '../ui/dialog'
import { Icon } from '../ui/icon'
import { Cosmetic } from '@prisma/client'

interface ParsedImages {
	featured?: string
	icon?: string
	smallIcon?: string
	lego?: {
		small?: string
		large?: string
	}
	bean?: {
		small?: string
		large?: string
	}
}

interface ParsedVariant {
	name?: string
	image?: string
}

interface IntroducedIn {
	text?: string
}

export function CosmeticHero2({ cosmetic }: { cosmetic: Cosmetic | null }) {
	const [sliderOpen, setSliderOpen] = useState(false)
	const [currentImageIndex, setCurrentImageIndex] = useState(0)

	const parsedImages: ParsedImages = cosmetic?.images
		? (JSON.parse(cosmetic.images) as ParsedImages)
		: {}
	const parsedVariants: { options?: ParsedVariant[] }[] = cosmetic?.variants
		? (JSON.parse(cosmetic.variants) as { options?: ParsedVariant[] }[])
		: []
	const parsedIntroducedIn: IntroducedIn = cosmetic?.introducedIn
		? (JSON.parse(cosmetic.introducedIn) as IntroducedIn)
		: {}

	const allImages = [
		{
			name: 'Featured',
			image: parsedImages.featured,
		},
		{ name: 'Icon', image: parsedImages.icon },
		{ name: 'Small Icon', image: parsedImages.smallIcon },
		{ name: 'LEGO Small', image: parsedImages.lego?.small },
		{ name: 'LEGO Large', image: parsedImages.lego?.large },
		{ name: 'Bean Small', image: parsedImages.bean?.small },
		{ name: 'Bean Large', image: parsedImages.bean?.large },
		...(parsedVariants[0]?.options || []).map(variant => ({
			name: variant?.name ?? '',
			image: variant?.image ?? '',
		})),
	].filter(img => img.image) // Filter out images without a valid URL

	const openSlider = (index: number) => {
		setCurrentImageIndex(index)
		setSliderOpen(true)
	}

	const nextImage = () => {
		setCurrentImageIndex(prevIndex => (prevIndex + 1) % allImages.length)
	}

	const prevImage = () => {
		setCurrentImageIndex(
			prevIndex => (prevIndex - 1 + allImages.length) % allImages.length,
		)
	}

	if (!cosmetic) {
		return <div>Loading...</div>
	}
	console.log(parsedImages)
	return (
		<div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-purple-700 to-indigo-900 p-4">
			<Card className="mx-auto w-full max-w-6xl border-2 border-purple-500 bg-black/50 text-white shadow-xl backdrop-blur-md">
				<CardContent className="p-0">
					<div className="grid grid-cols-1 gap-0 md:grid-cols-2">
						<div className="relative h-full min-h-[400px] md:min-h-[600px]">
							<img
								src={
									parsedImages.featured
										? parsedImages.featured
										: parsedImages.icon || '/placeholder.svg'
								}
								alt={cosmetic.name || 'Cosmetic'}
								className="h-full w-full rounded-t-lg object-cover md:rounded-l-lg md:rounded-tr-none"
							/>
						</div>
						<div className="space-y-8 p-8">
							<div className="space-y-4">
								<h1 className="text-4xl font-extrabold tracking-tight">
									{cosmetic.name}
								</h1>
								<p className="text-xl font-semibold text-purple-300">
									{cosmetic.description || 'No description available'}
								</p>
							</div>

							<div className="space-y-6">
								<h2 className="text-2xl font-bold">Details</h2>
								<div className="grid grid-cols-2 gap-4 text-sm">
									<div>
										Type:{' '}
										<span className="font-medium text-purple-300">
											{cosmetic.type}
										</span>
									</div>
									<div>
										Rarity:{' '}
										<span className="font-medium text-purple-300">
											{cosmetic.rarity}
										</span>
									</div>
									<div>
										Series:{' '}
										<span className="font-medium text-purple-300">
											{cosmetic.series || 'N/A'}
										</span>
									</div>
									<div>
										Set:{' '}
										<span className="font-medium text-purple-300">
											{cosmetic.set || 'N/A'}
										</span>
									</div>
									<div>
										Added:{' '}
										<span className="font-medium text-purple-300">
											{new Date(cosmetic.added).toLocaleDateString()}
										</span>
									</div>
									<div>
										Introduced:{' '}
										<span className="font-medium text-purple-300">
											{parsedIntroducedIn.text || 'Unknown'}
										</span>
									</div>
								</div>
							</div>

							<div className="space-y-6">
								<h2 className="text-2xl font-bold">Tags</h2>
								<div className="space-y-4">
									<div>
										<h3 className="text-lg font-semibold">Gameplay Tags</h3>
										<div className="mt-2 flex flex-wrap gap-2">
											{cosmetic.gameplayTags.split(',').map((tag, index) => (
												<Badge
													key={index}
													variant="secondary"
													className="bg-purple-500/50 text-white"
												>
													{tag.trim()}
												</Badge>
											))}
										</div>
									</div>
									<div>
										<h3 className="text-lg font-semibold">Meta Tags</h3>
										<div className="mt-2 flex flex-wrap gap-2">
											{cosmetic.metaTags.split(',').map((tag, index) => (
												<Badge
													key={index}
													variant="secondary"
													className="bg-purple-500/50 text-white"
												>
													{tag.trim()}
												</Badge>
											))}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					{cosmetic.showcaseVideo && (
						<div className="bg-black/60 p-8">
							<h2 className="mb-6 text-center text-2xl font-bold">
								{`${cosmetic.name} Video`}
							</h2>
							<div className="mx-auto max-w-4xl">
								<div className="aspect-w-16 aspect-h-9 ">
									<iframe
										src={`https://www.youtube.com/embed/${cosmetic.showcaseVideo}`}
										title={`${cosmetic.name} Showcase`}
										allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
										allowFullScreen
										className="min-h-[400px] w-full rounded-lg"
									></iframe>
								</div>
							</div>
						</div>
					)}

					{/* <div className="bg-black/70 p-8">
						<h2 className="mb-6 text-2xl font-bold">Images & Variants</h2>
						<Tabs defaultValue="grid" className="w-full">
							<TabsList className="mb-4 grid w-full grid-cols-2">
								<TabsTrigger value="grid">Grid View</TabsTrigger>
								<TabsTrigger value="masonry">Masonry View</TabsTrigger>
							</TabsList>
							<TabsContent value="grid" className="w-full">
								<div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
									{allImages.map((img, index) => (
										<button
											key={index}
											className="relative w-full overflow-hidden rounded-lg pt-[100%] transition-opacity hover:opacity-80"
											onClick={() => openSlider(index)}
										>
											<img
												src={img.image}
												alt={img.name}
												className="absolute left-0 top-0 h-full w-full object-cover"
											/>
											<div className="absolute bottom-0 left-0 right-0 bg-black/60 p-2 text-sm text-white">
												{img.name}
											</div>
										</button>
									))}
								</div>
							</TabsContent>
							<TabsContent value="masonry" className="w-full">
								<div className="columns-2 gap-4 space-y-4 sm:columns-3 md:columns-4">
									{allImages.map((img, index) => (
										<button
											key={index}
											className="relative block w-full overflow-hidden rounded-lg transition-opacity hover:opacity-80"
											onClick={() => openSlider(index)}
										>
											<img src={img.image} alt={img.name} className="w-full" />
											<div className="absolute bottom-0 left-0 right-0 bg-black/60 p-2 text-sm text-white">
												{img.name}
											</div>
										</button>
									))}
								</div>
							</TabsContent>
						</Tabs>
					</div> */}
				</CardContent>
			</Card>

			<Dialog open={sliderOpen} onOpenChange={setSliderOpen}>
				<DialogContent className="max-h-[90vh] max-w-[90vw] bg-black/90 p-0">
					<div className="relative flex h-full w-full items-center justify-center">
						<img
							src={allImages[currentImageIndex]?.image || '/placeholder.svg'}
							alt={allImages[currentImageIndex]?.name || 'Image'}
							className="max-h-full max-w-full object-contain"
						/>
						<div className="absolute bottom-4 left-0 right-0 text-center text-xl font-semibold text-white">
							{allImages[currentImageIndex]?.name || 'Image'}
						</div>
						<button
							className="absolute right-4 top-4 text-white hover:text-gray-300"
							onClick={() => setSliderOpen(false)}
						>
							<Icon name="cross-1" />
						</button>
						<button
							className="absolute left-4 top-1/2 -translate-y-1/2 transform text-white hover:text-gray-300"
							onClick={prevImage}
						>
							<Icon name="arrow-left" />
						</button>
						<button
							className="absolute right-4 top-1/2 -translate-y-1/2 transform text-white hover:text-gray-300"
							onClick={nextImage}
						>
							<Icon name="arrow-right" />
						</button>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	)
}
